import React, { useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { GoogleMap, Marker, Circle, OverlayView } from '@react-google-maps/api';
import useGoogleMapLoad from '../../../Components/useGoogleMapLoad';
import MapModalIcon from '../../../assets/images/icons/MapModal.svg';
import pinIcon from '../../../assets/images/icons/pin.svg';
import { mapStyles } from '../../../helpers/contants';

const containerStyle = {
  width: '97%',
  height: '480px',
  borderRadius: '12px',
  marginLeft: '14px',
};

//  Helper: Check if coordinates are within approximate USA bounds
const isWithinUSA = (lat, lng) => {
  // Rough bounding box for mainland USA
  return (
    lat >= 24.396308 && lat <= 49.384358 && lng >= -125.0 && lng <= -66.93457
  );
};

const MapModal = ({ open, handleClose, zoneData, userLocation }: any) => {
  const [isGoogleMapLoad] = useGoogleMapLoad();
  const mapRef = useRef<google.maps.Map | null>(null);

  const onLoad = (map: google.maps.Map) => {
    mapRef.current = map;

    const zoneLat = zoneData.center.latitude;
    const zoneLng = zoneData.center.longitude;
    const zoneLatLng = new window.google.maps.LatLng(zoneLat, zoneLng);

    const hasUser = Boolean(userLocation?.location?.coordinates);

    if (hasUser) {
      const userLng = userLocation.location.coordinates[0];
      const userLat = userLocation.location.coordinates[1];
      const userLatLng = new window.google.maps.LatLng(userLat, userLng);

      const bothInUSA =
        isWithinUSA(zoneLat, zoneLng) && isWithinUSA(userLat, userLng);

      if (bothInUSA) {
        //  If both inside the USA → show both locations together
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(zoneLatLng);
        bounds.extend(userLatLng);
        map.fitBounds(bounds);
      } else {
        //  Otherwise focus only on zone
        map.setCenter(zoneLatLng);
        map.setZoom(10);
      }
    }
  };

  const onUnmount = () => {
    mapRef.current = null;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Zone Location
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          ×
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ height: '500px', padding: 0 }}>
        {isGoogleMapLoad ? (
          <GoogleMap
            onLoad={onLoad}
            onUnmount={onUnmount}
            mapContainerStyle={containerStyle}
            options={{
              styles: mapStyles,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
              zoomControl: true,
            }}
          >
            {/* Zone Circle */}
            <Circle
              center={{
                lat: zoneData.center.latitude,
                lng: zoneData.center.longitude,
              }}
              radius={zoneData.radius}
              options={{
                strokeColor: '#5089AC',
                fillColor: 'transparent',
                fillOpacity: 0.3,
                strokeWeight: 4,
              }}
            />

            {/* Zone Marker */}
            <Marker
              position={{
                lat: zoneData.center.latitude,
                lng: zoneData.center.longitude,
              }}
              icon={MapModalIcon}
              title={zoneData.label}
            />

            <OverlayView
              position={{
                lat: zoneData.center.latitude,
                lng: zoneData.center.longitude,
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={() => ({ x: -80, y: 5 })}
            >
              <div className="custom-info-bubble">
                <span>{zoneData.label}</span>
              </div>
            </OverlayView>

            {/* User Marker */}
            {userLocation?.location?.coordinates && (
              <>
                <Marker
                  position={{
                    lat: userLocation.location.coordinates[1],
                    lng: userLocation.location.coordinates[0],
                  }}
                  icon={pinIcon}
                />
                <OverlayView
                  position={{
                    lat: userLocation.location.coordinates[1],
                    lng: userLocation.location.coordinates[0],
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={() => ({ x: -80, y: 5 })}
                >
                  <div className="custom-info-bubble">
                    <span>{userLocation?.locationName}</span>
                  </div>
                </OverlayView>
              </>
            )}
          </GoogleMap>
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Loading Map...
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MapModal;
